import { projectOrg } from '@/components/project/ProjectIcon';

/**
 * A–Z the way people read it: "Phase 2" before "Phase 10", case and accents
 * ignored. A plain localeCompare puts "Site 10" ahead of "Site 2".
 */
export const byName = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare;

/** The org a project is FOR, as every screen names it (its client, else its owner). */
export function orgOf(project: any): { id: string; name: string } {
  const org = projectOrg(project);
  return { id: org?.id ?? 'none', name: org?.name || 'Other' };
}

export interface OrgGroup<P> {
  orgId: string;
  orgName: string;
  projects: P[];
}

/** Projects grouped by organization: organizations A–Z, projects A–Z inside each. */
export function groupByOrg<P extends { name: string }>(projects: P[]): OrgGroup<P>[] {
  const groups = new Map<string, OrgGroup<P>>();
  for (const project of projects) {
    const { id, name } = orgOf(project);
    const group = groups.get(id) ?? { orgId: id, orgName: name, projects: [] };
    group.projects.push(project);
    groups.set(id, group);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, projects: [...g.projects].sort((a, b) => byName(a.name, b.name)) }))
    .sort((a, b) => byName(a.orgName, b.orgName));
}

// ---------------------------------------------------------------------------
// Recently opened projects (per browser). Drives where "/" lands and which
// project an organization switch opens.
// ---------------------------------------------------------------------------

const RECENT_KEY = 'solid-hub-recent-projects';
const MAX_RECENT = 10;

export function readRecentProjects(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function pushRecentProject(id: string): void {
  try {
    const recent = readRecentProjects().filter((x) => x !== id);
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...recent].slice(0, MAX_RECENT)));
  } catch {
    // Storage unavailable (private window): landing just falls back to favorites
  }
}

/**
 * The project to open from a set: the one opened most recently, else the
 * first favorite, else the first A–Z. Never guesses outside the set.
 */
export function bestProject<P extends { id: string; name: string; isFavorited?: boolean }>(projects: P[]): P | undefined {
  const recent = readRecentProjects();
  const byRecency = recent.map((id) => projects.find((p) => p.id === id)).find(Boolean);
  if (byRecency) return byRecency;
  const sorted = [...projects].sort((a, b) => byName(a.name, b.name));
  return sorted.find((p) => p.isFavorited) ?? sorted[0];
}
